export function toCapitalized(str: string): string {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
}

export function toNounCasing(str: string): string {
  //uppercase the first letter, lowerize all others, split on space
  return str
    .split(' ')
    .map((word) => {
      //special case for abbreviations
      //capitalized abbrevation
      if (word.length < 4 && word.toUpperCase() === word) {
        return word
      }

      //abbreviation with a period already noted
      if (word.length <= 4 && word.endsWith('.')) {
        return word
      }

      //word can be noun-casified
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    })
    .join(' ')
}

export function containsNumber(str: string): boolean {
  return /\d/.test(str)
}

export enum SequenceChangeType {
  Insertion = 'insertion',
  Deletion = 'deletion',
  Substitution = 'substitution',
}

export interface SequenceChange {
  origin: string | null | undefined
  dest: string | null | undefined
  type: SequenceChangeType
  index: number
}

export interface AlignedComparisons {
  leftShift: number
  ls: string
  ss: string
}

const getAlignments = (
  string1: string,
  string2: string,
): AlignedComparisons[] => {
  const longerString = string1.length > string2.length ? string1 : string2
  const shorterString = string1.length > string2.length ? string2 : string1

  const alignments: AlignedComparisons[] = []
  let nextLongerString = longerString
  let nextShorterString = shorterString
  let lsLenTotal = 0
  let ssLenTotal = 0
  while (true) {
    let leftShift = -1
    let lsMatch = ''
    let ssMatch = ''

    for (
      let slice = nextShorterString.length;
      slice > 0 && leftShift === -1;
      slice--
    ) {
      for (
        let i = 0;
        i < nextShorterString.length - slice && leftShift === -1;
        i++
      ) {
        const subSS = nextShorterString.substring(i, i + slice + 1)
        const dt = nextLongerString.length - subSS.length
        for (let j = 0; j <= dt; j++) {
          const subLS = nextLongerString.substring(j, subSS.length + j)
          if (subSS === subLS) {
            lsMatch = nextLongerString.substring(0, subSS.length + j)
            ssMatch = nextShorterString.substring(0, subSS.length + i)

            //build the next strings
            nextLongerString = nextLongerString.substring(
              subSS.length + j,
              nextLongerString.length,
            )
            nextShorterString = nextShorterString.substring(
              i + subSS.length,
              nextShorterString.length,
            )

            leftShift = j - i
            break
          }
        }
      }
    }

    if (leftShift === -1) {
      leftShift = 0
      lsMatch = nextLongerString
      ssMatch = nextShorterString
    }

    lsLenTotal += lsMatch.length
    ssLenTotal += ssMatch.length

    //push current alignment
    alignments.push({
      leftShift: leftShift,
      ls: lsMatch,
      ss: ssMatch,
    })

    if (
      lsLenTotal === longerString.length &&
      ssLenTotal === shorterString.length
    ) {
      break
    }
  }

  return alignments
}

const getComparisonString = (alignment: AlignedComparisons) => {
  const ls = alignment.ls
  const ss = alignment.ss
  const leftShift = alignment.leftShift
  const comparisonString: (string | null)[] = [...ss]
  for (let i = 0; i < ls.length; i++) {
    if (i < leftShift) {
      comparisonString.unshift(null)
      continue
    }

    const remaining = ls.length - comparisonString.length
    if (remaining > 0) {
      comparisonString.push(null)
    }
  }

  return comparisonString
}

const getSequenceChanges = (
  comparison: (string | null)[],
  longerString: string,
  longerOrigin: boolean,
) => {
  const cDiff: SequenceChange[] = []
  for (let i = 0; i < comparison.length; i++) {
    const lStringVal = longerString[i]
    const cStringVal = comparison[i]

    //compare comparison string to the longer string to build the diff, diff is conditional on direction
    if (lStringVal === cStringVal) {
      //no change
      continue
    }

    if (cStringVal === null) {
      cDiff.push({
        origin: longerOrigin ? lStringVal : cStringVal,
        dest: longerOrigin ? cStringVal : lStringVal,
        type: longerOrigin
          ? SequenceChangeType.Deletion
          : SequenceChangeType.Insertion,
        index: i,
      })

      continue
    }

    if (longerString.length <= i) {
      //the shift put us past the end of the longer string, account for this
      cDiff.push({
        origin: longerOrigin ? lStringVal : cStringVal,
        dest: longerOrigin ? cStringVal : lStringVal,
        type: longerOrigin
          ? SequenceChangeType.Insertion
          : SequenceChangeType.Deletion,
        index: i,
      })

      continue
    }

    if (cStringVal !== null && lStringVal !== null) {
      cDiff.push({
        origin: longerOrigin ? lStringVal : cStringVal,
        dest: longerOrigin ? cStringVal : lStringVal,
        type: SequenceChangeType.Substitution,
        index: i,
      })
    }
  }

  return cDiff
}

export const getDiff = (origin: string, dest: string) => {
  if (origin === dest) {
    return []
  }

  //which are we sliding to match?
  const shorterString = origin.length > dest.length ? dest : origin
  const longerString = origin.length > dest.length ? origin : dest

  const delta = longerString.length - shorterString.length
  if (delta === 0) {
    //strings are the same length
    //check for substitutions
    const diff: SequenceChange[] = []
    for (let i = 0; i < origin.length; i++) {
      if (origin[i] !== dest[i]) {
        diff.push({
          origin: origin[i],
          dest: dest[i],
          type: SequenceChangeType.Substitution,
          index: i,
        })
      }
    }

    return diff
  }

  const alignments = getAlignments(longerString, shorterString)

  let accumulatedLength = 0
  const diffs = alignments.map((alignment) => {
    const comparison = getComparisonString(alignment)
    const longerOrigin = longerString === origin
    const sequenceChanges = getSequenceChanges(
      comparison,
      alignment.ls,
      longerOrigin,
    )

    if (accumulatedLength > 0) {
      for (const sq of sequenceChanges) {
        sq.index = sq.index + accumulatedLength
      }
    }

    accumulatedLength += alignment.ls.length
    return sequenceChanges
  })

  return diffs.flat()
}
